import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from '@/components/TradingTrustStrip';
import { FeatureBadges } from '@/components/MicroTrustBadges';
import { Shield, FileText, Users, CheckCircle, AlertTriangle, Scale, Eye, Lock, Download, Building2, FileCheck, Banknote } from "lucide-react";

const Compliance = () => {
  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="Compliance & Risk Management | KYC/AML | Sanctions Screening | Padmora"
        description="Comprehensive compliance framework with KYC/AML procedures, sanctions screening, and risk management for secure commodity trading under ICC rules and international standards."
        image="/og-compliance.jpg"
        canonical="https://padmora.com/compliance"
        keywords={[
          "compliance policies",
          "KYC AML procedures",
          "sanctions screening",
          "risk management",
          "trade compliance",
          "international standards",
          "ICC compliance",
          "regulatory compliance",
          "financial compliance",
          "commodity trading compliance",
          "anti-money laundering",
          "know your customer",
          "trade sanctions"
        ]}
        aiOptimization={{
          aiKeywords: [
            "automated KYC verification",
            "AI sanctions screening",
            "machine learning risk assessment",
            "intelligent compliance monitoring",
            "automated regulatory reporting"
          ],
          entities: [
            "KYC",
            "AML",
            "Sanctions Screening",
            "Risk Management",
            "ICC Rules",
            "United States",
            "Compliance Framework"
          ],
          topics: [
            "Regulatory Compliance",
            "Risk Management",
            "Financial Compliance",
            "KYC Procedures",
            "Sanctions Compliance"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Compliance & Risk Management",
          "description": "Comprehensive compliance framework with KYC/AML procedures, sanctions screening, and risk management for secure commodity trading.",
          "mainEntity": {
            "@type": "CreativeWork",
            "name": "Padmora Compliance Framework",
            "description": "Official compliance policies and procedures for international commodity trading",
            "about": [
              {
                "@type": "Thing",
                "name": "KYC/AML",
                "description": "Know Your Customer and Anti-Money Laundering procedures"
              },
              {
                "@type": "Thing",
                "name": "Sanctions Screening",
                "description": "OFAC and international sanctions compliance"
              },
              {
                "@type": "Thing",
                "name": "Risk Management",
                "description": "Comprehensive risk assessment and mitigation strategies"
              }
            ],
            "hasPart": [
              {
                "@type": "CreativeWork",
                "name": "Compliance Policies",
                "description": "Detailed compliance policies and procedures"
              },
              {
                "@type": "CreativeWork",
                "name": "Risk Assessment",
                "description": "Risk management and assessment framework"
              }
            ]
          },
          "publisher": {
            "@type": "Organization",
            "name": "Padmora",
            "url": "https://padmora.com"
          }
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Compliance", url: "https://padmora.com/compliance" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              Compliance & Risk Management
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              KYC/AML, Sanctions screening, and bank-grade compliance for secure commodity trading
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Start Compliance Process
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg border-blue-600 text-blue-600 hover:bg-blue-50"
                asChild
              >
                <a href="/lc-policy">
                  Learn About LC Policy
                </a>
              </Button>
            </motion.div>
          </motion.div>

                    <TradingTrustStrip />

          {/* Partners */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Compliance Areas */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            Compliance Framework
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">KYC & AML</h3>
              <p className="text-slate-600 mb-4">
                Comprehensive Know Your Customer and Anti-Money Laundering procedures
              </p>
              <FeatureBadges
                features={[
                  { icon: <FileText className="h-4 w-4" />, label: "Identity verification" },
                  { icon: <Eye className="h-4 w-4" />, label: "Source of funds check" },
                  { icon: <Shield className="h-4 w-4" />, label: "AML screening" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Sanctions Screening</h3>
              <p className="text-slate-600 mb-4">
                OFAC, EU, UN sanctions lists screening for all counterparties
              </p>
              <FeatureBadges
                features={[
                  { icon: <AlertTriangle className="h-4 w-4" />, label: "OFAC compliance" },
                  { icon: <Shield className="h-4 w-4" />, label: "EU sanctions" },
                  { icon: <CheckCircle className="h-4 w-4" />, label: "UN restricted" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-left bg-card/50 border border-border/50 p-8 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Scale className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Anti-Bribery</h3>
              <p className="text-slate-600 mb-4">
                FCPA and UK Bribery Act compliance with corruption prevention
              </p>
              <FeatureBadges
                features={[
                  { icon: <Scale className="h-4 w-4" />, label: "FCPA compliance" },
                  { icon: <Lock className="h-4 w-4" />, label: "Bribery prevention" },
                  { icon: <Shield className="h-4 w-4" />, label: "Corruption screening" }
                ]}
                align="left"
                size="sm"
                className="mt-4"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="text-3xl font-bold text-center mb-12"
          >
            Compliance Process
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto"
          >
            <motion.div
              variants={fadeInUp}
              className="text-center bg-white border border-border/50 p-6 rounded-lg shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-lg font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">Initial Screening</h3>
              <p className="text-slate-600 text-sm">
                Basic sanctions and entity matching before engagement
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-center bg-white border border-border/50 p-6 rounded-lg shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-lg font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">KYC Documentation</h3>
              <p className="text-slate-600 text-sm">
                Company registration, beneficial ownership, and business verification
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-center bg-white border border-border/50 p-6 rounded-lg shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-lg font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">Enhanced Due Diligence</h3>
              <p className="text-slate-600 text-sm">
                Deep sanctions screening and source of funds verification
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="text-center bg-white border border-border/50 p-6 rounded-lg shadow-sm"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <span className="text-lg font-bold text-blue-600">4</span>
              </div>
              <h3 className="text-lg font-semibold mb-3">Ongoing Monitoring</h3>
              <p className="text-slate-600 text-sm">
                Continuous compliance monitoring and transaction screening
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Document Downloads */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-8"
            >
              Compliance Resources
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-center text-slate-600 mb-12"
            >
              Download our compliance policies and procedures (shared under NDA)
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  AML/KYC Policy
                </h3>
                <p className="text-slate-600 text-sm">
                  Comprehensive anti-money laundering and know your customer procedures
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Sanctions Policy
                </h3>
                <p className="text-slate-600 text-sm">
                  International sanctions screening and compliance procedures
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900 flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Data Protection Policy
                </h3>
                <p className="text-slate-600 text-sm">
                  Data handling, privacy, and information security procedures
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card/50 border border-border/50 p-6 rounded-lg shadow-sm"
              >
                <h3 className="text-lg font-semibold mb-3 text-slate-900 flex items-center gap-2">
                  <Scale className="h-5 w-5" />
                  Anti-Bribery Policy
                </h3>
                <p className="text-slate-600 text-sm">
                  Corruption prevention and anti-bribery compliance framework
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              Ready to Start Compliance?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Begin your KYC process and access our secure trading platform with bank-grade compliance.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                size="lg"
                variant="default"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  Start Compliance Process
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Compliance;
